import { useCallback, useEffect, useRef, useState } from "react";
import { Form, Formik } from "formik";
import type {
  ICupomLoja,
  ICategoriaProduto,
  IProdutoLoja,
} from "interfaces/loja";
import type { ICupomLojaCreate, ICupomUpdate } from "interfaces/loja";
import { useMaterialUIController, setMessage } from "context";
import {
  createCupomLoja,
  updateCupomLoja,
  vincularCupomProdutoLoja,
  desvincularCupomProdutoLoja,
  desvincularCupomVariacaoLoja,
  vincularCupomCategoriaLoja,
  desvincularCupomCategoriaLoja,
} from "services/lojaCupom";
import { getCategoriasArvore } from "services/lojaCategorias";
import {
  listarProdutosLojaLotePorIds,
  listarProdutosPublicos,
} from "services/lojaProduto";
import CupomFormDados, { FORM_ID } from "./CupomFormDados";
import CupomFormVinculos, {
  buildVinculosLinhasFromCupom,
  type OpcaoVinculo,
  type VinculoLinhaCupom,
} from "./CupomFormVinculos";
import {
  initialValues,
  initialValuesEdit,
  type CupomFormValues,
} from "./schemas/initialValues";
import { validationSchemaCreate } from "./schemas/validations";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import CircularProgress from "@mui/material/CircularProgress";
import CloseIcon from "@mui/icons-material/Close";
import ConfirmationNumberOutlinedIcon from "@mui/icons-material/ConfirmationNumberOutlined";
import LinkOutlinedIcon from "@mui/icons-material/LinkOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import { Box, IconButton, Stack, Typography } from "@mui/material";
import {
  cupomTabsSx,
  modalHeaderSx,
  modalPaperSx,
  saveButtonSx,
} from "./cupons-loja.theme";

const LIMITE_BUSCA = 10;

function normalizeCupomPayload(raw: unknown): ICupomLoja {
  if (
    raw &&
    typeof raw === "object" &&
    "data" in raw &&
    (raw as { data?: ICupomLoja }).data != null
  ) {
    return (raw as { data: ICupomLoja }).data;
  }
  return raw as ICupomLoja;
}

function isHttpOk(status: number) {
  return status >= 200 && status < 300;
}

function buildPayload(values: CupomFormValues): ICupomLojaCreate {
  const codigo = String(values.codigo).trim().toUpperCase().replace(/\s/g, "");
  const valor = Number(String(values.valor).replace(/,/g, "."));
  const inicioEm = values.inicioEm
    ? `${values.inicioEm}T00:00:00.000Z`
    : undefined;
  const fimEm = values.fimEm ? `${values.fimEm}T23:59:59.000Z` : undefined;
  const parseOpt = (v: string) => {
    if (v == null || v === "") return undefined;
    const n = Number(String(v).replace(/,/g, "."));
    return Number.isNaN(n) ? undefined : n;
  };
  const payload: ICupomLojaCreate = {
    codigo,
    tipo: values.tipo as "PORCENTAGEM" | "VALOR_FIXO",
    valor,
    ativo: values.ativo,
    inicioEm: inicioEm || null,
    fimEm: fimEm || null,
    subtotalMinimo: parseOpt(values.subtotalMinimo) ?? null,
    limiteUsoGlobal: parseOpt(values.limiteUsoGlobal) ?? null,
    limiteUsoPorUsuario: parseOpt(values.limiteUsoPorUsuario) ?? null,
  };
  return payload;
}

export interface CupomModalProps {
  open: boolean;
  editing: ICupomLoja | null;
  onClose: () => void;
  onSuccess: () => void;
}

export default function CupomModal({
  open,
  editing,
  onClose,
  onSuccess,
}: CupomModalProps) {
  const [, dispatch] = useMaterialUIController();
  const [tab, setTab] = useState(0);
  const [opcaoVinculo, setOpcaoVinculo] = useState<OpcaoVinculo>("global");
  const [buscaProduto, setBuscaProduto] = useState("");
  const [produtosBusca, setProdutosBusca] = useState<IProdutoLoja[]>([]);
  const [pageBusca, setPageBusca] = useState(1);
  const [hasMaisBusca, setHasMaisBusca] = useState(false);
  const [produtosInfo, setProdutosInfo] = useState<
    Record<string, { nome: string; sku?: string }>
  >({});

  const [vinculosLinhas, setVinculosLinhas] = useState<VinculoLinhaCupom[]>([]);
  const [categoriasVinculadas, setCategoriasVinculadas] = useState<number[]>(
    []
  );
  const [categoriasArvore, setCategoriasArvore] = useState<ICategoriaProduto[]>(
    []
  );

  const [vincLoading, setVincLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const debounceRef = useRef<number | null>(null);

  const cupomId = editing?.id ?? null;

  function resetState() {
    setTab(0);
    setOpcaoVinculo("global");
    setBuscaProduto("");
    setProdutosBusca([]);
    setPageBusca(1);
    setHasMaisBusca(false);
    setVinculosLinhas([]);
    setProdutosInfo({});
    setCategoriasArvore([]);
    setCategoriasVinculadas([]);
  }

  function handleClose() {
    resetState();
    onClose();
  }

  useEffect(() => {
    if (!open || !editing?.id) return;
    loadVinculos(String(editing.id));
  }, [open, editing?.id]);

  async function loadVinculos(_cupomId: string) {
    try {
      const linhas = editing ? buildVinculosLinhasFromCupom(editing) : [];
      const produtoIds = [...new Set(linhas.map((l) => l.produtoId))];
      const categoriasIds =
        editing?.cupomCategorias.map(({ categoriaId }) => categoriaId) ?? [];

      setVincLoading(true);
      setVinculosLinhas(linhas);
      setCategoriasVinculadas(categoriasIds);

      const { data: categoryTree } = await getCategoriasArvore();
      setCategoriasArvore(categoryTree);

      if (produtoIds.length) await loadProdutosInfo(produtoIds);

      const tipoVinculo =
        linhas.length > 0
          ? "produtos"
          : categoriasIds.length > 0
            ? "categorias"
            : "global";

      setOpcaoVinculo(tipoVinculo);
    } catch (e: any) {
      setMessage(dispatch, {
        open: true,
        severity: "error",
        text: e.message || "Falha ao carregar vínculos.",
      });
    } finally {
      setVincLoading(false);
    }
  }

  async function loadProdutosInfo(ids: string[]) {
    if (!ids.length) return;
    try {
      const { data: produtos, status } = await listarProdutosLojaLotePorIds(ids);
      if (status < 200 || status >= 300 || !Array.isArray(produtos)) {
        throw new Error("Resposta inválida ao carregar produtos vinculados.");
      }

      const updates: Record<string, { nome: string; sku?: string }> = {};
      for (const produto of produtos) {
        updates[String(produto.id)] = {
          nome: produto.nome,
          sku: produto.sku,
        };
      }
      setProdutosInfo((prev) => ({ ...prev, ...updates }));
    } catch (e: unknown) {
      const msg =
        e instanceof Error ? e.message : "Falha ao carregar produtos vinculados.";
      setMessage(dispatch, {
        open: true,
        severity: "warning",
        text: msg,
      });
    }
  }

  const buscarProdutos = useCallback(
    async (pagina?: number) => {
      const paginaUsar = pagina ?? pageBusca;
      try {
        setVincLoading(true);
        const { data, status } = await listarProdutosPublicos({
          query: buscaProduto,
          limit: LIMITE_BUSCA,
          page: paginaUsar,
        });

        if (status === 200) {
          setProdutosBusca(data.data);
          setHasMaisBusca(data.data.length >= LIMITE_BUSCA);
          setPageBusca(paginaUsar);
        }
      } catch (e: any) {
        setMessage(dispatch, {
          open: true,
          severity: "error",
          text: e.message || "Falha na busca.",
        });
      } finally {
        setVincLoading(false);
      }
    },
    [buscaProduto, pageBusca]
  );

  useEffect(() => {
    const term = buscaProduto.trim();
    if (debounceRef.current) {
      window.clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
    if (term.length < 2) {
      setProdutosBusca([]);
      setHasMaisBusca(false);
      setPageBusca(1);
      return;
    }
    debounceRef.current = window.setTimeout(() => buscarProdutos(1), 400);
    return () => {
      if (debounceRef.current) {
        window.clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }
    };
  }, [buscaProduto, buscarProdutos]);

  async function onVincularBusca(spec: {
    produtoId: string;
    variacaoId?: string;
  }) {
    if (!cupomId) return;
    const cid = String(cupomId);
    try {
      const body =
        spec.variacaoId != null
          ? { produtoIds: [] as string[], variacaoIds: [spec.variacaoId] }
          : { produtoIds: [spec.produtoId], variacaoIds: [] as string[] };
      const { data, status } = await vincularCupomProdutoLoja(cid, body);
      if (!isHttpOk(status)) throw new Error("Falha ao vincular.");
      const cupom = normalizeCupomPayload(data);
      const novasLinhas = buildVinculosLinhasFromCupom(cupom);
      setVinculosLinhas(novasLinhas);
      const pids = [...new Set(novasLinhas.map((l) => l.produtoId))];
      if (pids.length) await loadProdutosInfo(pids);
      setMessage(dispatch, {
        open: true,
        severity: "success",
        text:
          spec.variacaoId != null
            ? "Variação vinculada."
            : "Produto vinculado.",
      });
    } catch (e: any) {
      setMessage(dispatch, {
        open: true,
        severity: "error",
        text: e.message || "Falha ao vincular.",
      });
    }
  }

  async function onDesvincularLinha(linha: VinculoLinhaCupom) {
    if (!cupomId) return;
    const cid = String(cupomId);
    try {
      const { data, status } =
        linha.tipo === "variacao"
          ? await desvincularCupomVariacaoLoja(cid, linha.variacaoId)
          : await desvincularCupomProdutoLoja(cid, linha.produtoId);

      if (!isHttpOk(status)) throw new Error("Falha ao desvincular.");

      const cupom = normalizeCupomPayload(data);
      const novasLinhas = buildVinculosLinhasFromCupom(cupom);
      setVinculosLinhas(novasLinhas);
      const pids = [...new Set(novasLinhas.map((l) => l.produtoId))];
      if (pids.length) await loadProdutosInfo(pids);

      setMessage(dispatch, {
        open: true,
        severity: "success",
        text: "Vínculo removido.",
      });
    } catch (e: any) {
      setMessage(dispatch, {
        open: true,
        severity: "error",
        text: e.message || "Falha ao desvincular.",
      });
    }
  }

  async function onToggleCategoria(categoriaId: number) {
    if (!cupomId) return;
    const cid = String(cupomId);
    const selected = categoriasVinculadas.includes(categoriaId);
    try {
      if (selected) {
        setCategoriasVinculadas((prev) =>
          prev.filter((id) => id !== categoriaId)
        );
        await desvincularCupomCategoriaLoja(cid, categoriaId);
        setMessage(dispatch, {
          open: true,
          severity: "success",
          text: "Categoria desvinculada.",
        });
      } else {
        setCategoriasVinculadas((prev) => [...prev, categoriaId]);
        await vincularCupomCategoriaLoja(cid, categoriaId);
        setMessage(dispatch, {
          open: true,
          severity: "success",
          text: "Categoria vinculada.",
        });
      }
    } catch (e: any) {
      if (selected) {
        setCategoriasVinculadas((prev) => [...prev, categoriaId]);
      } else {
        setCategoriasVinculadas((prev) =>
          prev.filter((id) => id !== categoriaId)
        );
      }
      setMessage(dispatch, {
        open: true,
        severity: "error",
        text: e.message || "Falha ao atualizar vínculo.",
      });
    }
  }

  async function handleSubmit(values: CupomFormValues) {
    setSaving(true);
    try {
      const payload = buildPayload(values);
      if (editing) {
        await updateCupomLoja(String(editing.id), payload as ICupomUpdate);
        setMessage(dispatch, {
          open: true,
          severity: "success",
          text: "Cupom atualizado.",
        });
      } else {
        await createCupomLoja(payload);
        setMessage(dispatch, {
          open: true,
          severity: "success",
          text: "Cupom criado.",
        });
      }
      handleClose();
      onSuccess();
    } catch (e: any) {
      setMessage(dispatch, {
        open: true,
        severity: "error",
        text: e.message || "Falha ao salvar cupom.",
      });
    } finally {
      setSaving(false);
    }
  }

  const vinculosDisabled = !cupomId;

  if (!open) return null;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={false}
      fullWidth
      scroll="paper"
      PaperProps={{ sx: modalPaperSx }}
    >
      <Box sx={modalHeaderSx}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2}>
          <Stack direction="row" spacing={2} alignItems="center" minWidth={0}>
            <ConfirmationNumberOutlinedIcon sx={{ fontSize: 36, opacity: 0.9 }} />
            <Box minWidth={0}>
              <Typography variant="overline" sx={{ opacity: 0.75, letterSpacing: 1.2 }}>
                Promoções da loja
              </Typography>
              <Typography variant="h5" fontWeight="bold" noWrap>
                {editing ? "Editar cupom" : "Novo cupom"}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.85, mt: 0.25 }}>
                {editing
                  ? "Ajuste regras, validade e vínculos"
                  : "Configure código, desconto e validade"}
              </Typography>
            </Box>
          </Stack>
          <IconButton onClick={handleClose} sx={{ color: "inherit" }} aria-label="Fechar">
            <CloseIcon />
          </IconButton>
        </Stack>
      </Box>

      <Formik<CupomFormValues>
        initialValues={editing ? initialValuesEdit(editing) : initialValues}
        validationSchema={validationSchemaCreate}
        onSubmit={handleSubmit}
      >
        <Form id={FORM_ID}>
          <DialogContent
            sx={{ p: 0, "&.MuiDialogContent-root": { paddingTop: 0 } }}
          >
            <MDBox px={3} pt={1.5}>
              <Tabs
                value={tab}
                onChange={(_, v) => setTab(v)}
                aria-label="tabs-cupom"
                sx={cupomTabsSx}
              >
                <Tab
                  icon={<ReceiptLongOutlinedIcon sx={{ fontSize: 18 }} />}
                  iconPosition="start"
                  label="Dados do cupom"
                />
                <Tab
                  icon={<LinkOutlinedIcon sx={{ fontSize: 18 }} />}
                  iconPosition="start"
                  label="Vínculos"
                  disabled={vinculosDisabled}
                />
              </Tabs>
            </MDBox>

            <MDBox
              sx={{
                bgcolor: "grey.50",
                borderTop: 1,
                borderColor: "divider",
                px: 3,
                py: 3,
                minHeight: 320,
              }}
            >
              <MDBox
                hidden={tab !== 0}
                sx={{
                  display: tab === 0 ? "block" : "none",
                  animation: "fadeIn 0.25s ease",
                  "@keyframes fadeIn": {
                    from: { opacity: 0 },
                    to: { opacity: 1 },
                  },
                }}
              >
                <CupomFormDados />
              </MDBox>
              <MDBox
                hidden={tab !== 1}
                sx={{
                  display: tab === 1 ? "block" : "none",
                  animation: "fadeIn 0.25s ease",
                  "@keyframes fadeIn": {
                    from: { opacity: 0 },
                    to: { opacity: 1 },
                  },
                }}
              >
                <CupomFormVinculos
                  disabled={vinculosDisabled}
                  opcaoVinculo={opcaoVinculo}
                  onOpcaoVinculoChange={setOpcaoVinculo}
                  buscaProduto={buscaProduto}
                  onBuscaProdutoChange={setBuscaProduto}
                  onBuscarProdutos={buscarProdutos}
                  produtosBusca={produtosBusca}
                  vinculosLinhas={vinculosLinhas}
                  produtosInfo={produtosInfo}
                  onVincularBusca={onVincularBusca}
                  onDesvincularLinha={onDesvincularLinha}
                  pageBusca={pageBusca}
                  hasMaisBusca={hasMaisBusca}
                  vincLoading={vincLoading}
                  categoriasArvore={categoriasArvore}
                  categoriasVinculadas={categoriasVinculadas}
                  onToggleCategoria={onToggleCategoria}
                />
              </MDBox>
            </MDBox>
          </DialogContent>

          <DialogActions
            sx={{
              px: 3,
              py: 2,
              gap: 1.5,
              borderTop: 1,
              borderColor: "divider",
              bgcolor: "background.paper",
            }}
          >
            <MDButton
              variant="outlined"
              color="dark"
              onClick={handleClose}
              sx={{ textTransform: "none", fontWeight: 500 }}
            >
              Cancelar
            </MDButton>
            <MDButton
              type="submit"
              variant="gradient"
              color="dark"
              disabled={saving}
              startIcon={
                saving ? (
                  <CircularProgress size={18} color="inherit" />
                ) : (
                  <SaveOutlinedIcon sx={{ fontSize: 18 }} />
                )
              }
              sx={saveButtonSx}
            >
              {saving ? "Salvando…" : "Salvar cupom"}
            </MDButton>
          </DialogActions>
        </Form>
      </Formik>
    </Dialog>
  );
}
